import { useToast } from "@chakra-ui/react";
import React, { useEffect, useRef } from "react";
import { BellIcon, ChatIcon } from "@chakra-ui/icons";
import { useSseStore } from "../../../stores/sse.store";
import { sseChannelUrl } from "./streamApiRoot";

const BASE_RECONNECT_DELAY_MS = 3 * 1000;
const MAX_RECONNECT_DELAY_MS = 60 * 1000;
/** A stream that stayed up this long counts as healthy; the next failure restarts the backoff. */
const HEALTHY_STREAM_MS = 30 * 1000;

interface Props {
	accessToken: string;
	organisationId: string;
}

/**
 * Headless component that keeps the organisation event stream open while the
 * user is logged in. Two jobs: show broadcast toasts, and report whether the
 * stream is up via `useSseStore` (the "online" dot in the side menu).
 *
 * Reconnects for as long as the component is mounted, with a backoff that
 * grows to a minute for a stream that keeps failing at once and resets after
 * one that stayed up. Also retries at once when the browser reports it is
 * back online or the tab becomes visible again, so the dot recovers quickly
 * after a laptop sleep.
 */
const SSE: React.FC<Props> = ({ accessToken, organisationId }) => {
	const toast = useToast();
	const setStatus = useSseStore((state) => state.setStatus);
	const eventSourceRef = useRef<EventSource | null>(null);
	const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const attemptsRef = useRef<number>(0);
	const openedAtRef = useRef<number>(0);

	// Disconnect from the SSE endpoint
	const disconnect = () => {
		if (reconnectTimeoutRef.current) {
			clearTimeout(reconnectTimeoutRef.current);
			reconnectTimeoutRef.current = null;
		}

		if (eventSourceRef.current) {
			eventSourceRef.current.close();
			eventSourceRef.current = null;
		}
	};

	const scheduleReconnect = () => {
		if (reconnectTimeoutRef.current) return;
		// A stream that lived a while was healthy: start the backoff over.
		attemptsRef.current =
			openedAtRef.current && Date.now() - openedAtRef.current > HEALTHY_STREAM_MS ? 1 : attemptsRef.current + 1;
		const delay = Math.min(MAX_RECONNECT_DELAY_MS, BASE_RECONNECT_DELAY_MS * 2 ** Math.min(attemptsRef.current - 1, 4));
		reconnectTimeoutRef.current = setTimeout(() => {
			reconnectTimeoutRef.current = null;
			connect();
		}, delay);
	};

	// Connect to the SSE endpoint
	const connect = () => {
		disconnect();
		setStatus("connecting");
		openedAtRef.current = 0;

		const urlWithAuth = sseChannelUrl(`org-notifs-${organisationId}`, accessToken);

		try {
			const eventSource = new EventSource(urlWithAuth);
			eventSourceRef.current = eventSource;

			eventSource.onopen = () => {
				openedAtRef.current = Date.now();
				setStatus("online");
			};

			eventSource.onmessage = (event) => {
				try {
					const data = JSON.parse(event.data);

					if (data.type === "error") {
						// api-gateway refused the stream (e.g. too many connections); it
						// closes right after this, and onerror schedules the retry.
						setStatus("offline");
						return;
					}

					if (data.message) {
						if (data.type === "notification") {
							toast({
								description: data.message,
								status: "info",
								duration: 5000,
								isClosable: true,
								position: "bottom-right",
								colorScheme: "gray",
								icon: <BellIcon boxSize={5} color="white" />,
							});
						} else if (data.type === "bot") {
							toast({
								title: "Robert", // Robert is the bot name (a.k.a. Bilda AI)
								description: data.message,
								status: "info",
								duration: 5000,
								isClosable: true,
								position: "bottom-right",
								colorScheme: "gray",
								icon: <ChatIcon boxSize={5} color="white" />,
							});
						}
					}
				} catch (error) {
					// Error processing SSE message
				}
			};

			eventSource.onerror = () => {
				disconnect();
				setStatus("offline");
				scheduleReconnect();
			};
		} catch (error) {
			setStatus("offline");
			scheduleReconnect();
		}
	};

	useEffect(() => {
		attemptsRef.current = 0;
		connect();

		// Retry at once when the browser comes back or the tab is looked at again.
		const retryNow = () => {
			if (document.visibilityState === "hidden") return;
			if (eventSourceRef.current?.readyState === EventSource.OPEN) return;
			attemptsRef.current = 0;
			connect();
		};
		window.addEventListener("online", retryNow);
		document.addEventListener("visibilitychange", retryNow);

		return () => {
			window.removeEventListener("online", retryNow);
			document.removeEventListener("visibilitychange", retryNow);
			disconnect();
			setStatus("offline");
		};
	}, [organisationId, accessToken]);

	// This is a headless component, so it doesn't render anything
	return null;
};

export default React.memo(SSE, (prevProps, nextProps) => {
	// Only re-render if accessToken or organisationId has changed
	return (
		prevProps.accessToken === nextProps.accessToken &&
		prevProps.organisationId === nextProps.organisationId
	);
});
